import { Component, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, MAX_VERBATIMS, ReportBlock, tint } from '../report-aggregation';

/**
 * Answers to a page when the block does not say. A row is a single line of small type, so a page
 * holds a screenful of them rather than the seven the old padded cards had room for.
 */
const DEFAULT_PAGE_SIZE = 15;

/** Below this there is nothing to search through, and the box is one more thing on the card. */
const SEARCH_WORTH_IT = 8;

/** How far the count pill's background is taken toward white from the deck's own colour. */
const PILL_TINT = 0.85;

/** One answer, or several respondents who gave the same one. */
interface Answer {
  /** The first spelling seen, shown as typed. */
  text: string;
  /** How many respondents gave it. 1 for most. */
  count: number;
  /** The text split around the search term, so a match can be marked without building HTML. */
  parts: { text: string; hit: boolean }[];
}

/**
 * What two answers have to share to be the same answer: case and inner spacing are noise here.
 *
 * Deliberately no further than that -- no punctuation stripping, no stemming. "None" and "none." are
 * plainly one answer; "no" and "not really" are not, and a normaliser confident enough to merge the
 * second pair would merge things nobody agreed to merge. The count under-reports rather than
 * over-reports, which is the right way round for a figure a client may quote.
 */
function sameAnswer(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Open-end explorer: the open-ended answers to one question, in their own words.
 *
 * Nothing here counts anything except repetition. Every other module in the deck turns answers into
 * a figure; this is where a reader goes to find out what the figure is made of, so the text is shown
 * as typed -- line breaks, capitals, run-on sentences and all.
 *
 * The answers arrive on the series, already narrowed by the deck's filter rail, so "what did *these*
 * people say" needs no wiring here -- it is the same records every chart on the page is drawn from.
 * Searching is over that filtered set and narrows further; it never reaches past the rail.
 */
@Component({
  selector: 'app-verbatim-explorer-block',
  standalone: false,
  template: `
    <div class="verbatims" [class.compact]="compact">
      <div class="head">
        <!-- "154 responses · How would you describe this brand to a friend?" -- how much there is
             to read, then what these people were asked. The question is dropped when the card is
             already titled with it, rather than saying it twice. -->
        @if (countLine || questionLine) {
          <p class="title">@if (countLine) {<span>{{ countLine }}</span>}@if (questionLine) {@if (countLine) {<span class="sep"> · </span>}<span>{{ questionLine }}</span>}</p>
        }

        @if (showSearch) {
          <div class="search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input type="search"
                   class="form-control form-control-sm"
                   [id]="'verbatim-search-' + block.id"
                   [attr.aria-label]="'Search answers to ' + question"
                   placeholder="Search these answers"
                   [value]="term"
                   (input)="search($any($event.target).value)">
          </div>
        }
      </div>

      <!-- Only when there is something the title cannot say: what a search matched, the cap, or
           why there is nothing to show. -->
      @if (note) {
        <p class="note mb-0">{{ note }}</p>
      }

      @if (shown.length) {
        <ul class="list list-unstyled mb-0">
          @for (answer of shown; track $index) {
            <li class="answer" [style.borderLeftColor]="palette.secondary">
              <!-- One line on purpose: a newline between the parts would be rendered inside the
                   answer and space out the middle of a word. -->
              <span class="text">@for (part of answer.parts; track $index) {@if (part.hit) {<mark class="hit">{{ part.text }}</mark>}@else {<span>{{ part.text }}</span>}}</span>
              @if (answer.count > 1) {
                <span class="count"
                      [style.background]="countFill"
                      [style.color]="palette.primary"
                      [attr.aria-label]="answer.count + ' respondents gave this answer'">
                  ×{{ answer.count }}
                </span>
              }
            </li>
          }
        </ul>

        @if (pages > 1) {
          <div class="pager">
            <button type="button" class="step btn btn-sm" [disabled]="page === 0" (click)="turn(-1)">
              <i class="fas fa-chevron-left" aria-hidden="true"></i> Previous
            </button>
            <!-- Which answers these are, not just which page: on a module whose whole job is "what
                 did people say", "41-50 of 412" is the more useful half of it. -->
            <span class="where">{{ range }}</span>
            <button type="button" class="step btn btn-sm"
                    [disabled]="page >= pages - 1" (click)="turn(1)">
              Next <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        }
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    /* The title on the left, the search on the right, wrapping under it once the card is too
       narrow for both -- a half-width card and the compare-by breakout both get there. */
    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 16px;
      margin-bottom: 12px;
    }
    .verbatims.compact .head { gap: 6px 10px; margin-bottom: 8px; }

    .title {
      flex: 1 1 18rem;
      min-width: 0;
      margin: 0;
      font-size: 15px;
      font-weight: 700;
      line-height: 1.3;
      color: var(--ecr-ink, #1b2b33);
    }
    .verbatims.compact .title { font-size: 13px; }

    .search {
      flex: 0 1 16rem;
      position: relative;
    }
    .search i {
      position: absolute;
      top: 50%;
      left: 10px;
      transform: translateY(-50%);
      font-size: 11px;
      color: var(--ecr-mut, #5a6b73);
      pointer-events: none;
    }
    /* Room for the icon, and the browser's own clear button left alone on the right. */
    .search input {
      height: auto;
      padding: 5px 10px 5px 28px;
      font-size: 12px;
      border-radius: 6px;
    }

    .note {
      font-size: 12px;
      line-height: 1.3;
      color: var(--ecr-mut, #5a6b73);
      margin-bottom: 8px !important;
    }

    /* A tinted row per answer with a rule down its side, tight to the next: read as one list of
       quotations rather than as a stack of cards. */
    .list {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .answer {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      border-left: 3px solid;
      background: var(--ecr-bg, #eef3f5);
      padding: 6px 12px;
      font-size: 12px;
      line-height: 1.45;
      color: var(--ecr-ink, #1b2b33);
    }
    .verbatims.compact .answer { font-size: 11px; padding: 4px 9px; }

    /* Respondents paste line breaks, type in capitals and run on. Kept as typed -- this is the one
       module whose content is not ours to tidy -- but wrapped so a pasted URL cannot widen the card
       past the page. */
    .text {
      flex: 1;
      min-width: 0;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    /* The search term, marked in place, in a wash of the deck's accent -- its colour for whatever is
       being called out. Neither the browser's yellow nor Navigator's warning cream: both belong to
       no palette, and the cream read as an error. */
    .hit {
      background: color-mix(in srgb, var(--ecr-accent) 24%, transparent);
      color: inherit;
      padding: 0 1px;
      border-radius: 2px;
    }

    /* Only ever drawn above 1, so it reads as "this was said more than once" rather than as a
       count column with a 1 in every row. */
    .count {
      flex: none;
      font-size: 11px;
      font-weight: 700;
      line-height: 1;
      padding: 3px 7px;
      border-radius: 10px;
      white-space: nowrap;
    }

    .pager {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 10px;
    }
    .step {
      border: 1px solid var(--ecr-line, #dbe4e8);
      border-radius: 6px;
      background: none;
      font-size: 12px;
      font-weight: 600;
      color: var(--ecr-ink, #1b2b33);
      padding: 4px 12px;
    }
    .step:hover:not(:disabled) { border-color: var(--ecr-grey, #c3ced3); }
    /* Kept in place rather than hidden at the ends, so the pager does not move under the cursor
       between the first page and the second. */
    .step:disabled { opacity: 0.4; }
    .where { font-size: 12px; color: var(--ecr-mut, #5a6b73); margin-left: auto; }
  `],
})
export class VerbatimExplorerBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;

  question = '';
  /** Blank where the card title already says it, so it is never on the card twice. */
  questionLine = '';
  /** "154 responses", leading the title. Blank when nobody answered. */
  countLine = '';
  /** What a search matched, the cap, or why there is nothing to show. Blank otherwise. */
  note = '';
  /** Every distinct answer the rail left, repeats folded together. */
  private answers: Answer[] = [];
  /** How many respondents answered, against how many answers the series carried. */
  private answered = 0;
  private capped = false;
  private matching: Answer[] = [];
  shown: Answer[] = [];
  term = '';
  page = 0;
  pages = 1;
  /** "41-50 of 412", the answers this page is showing. */
  range = '';
  pageSize = DEFAULT_PAGE_SIZE;
  showSearch = true;
  countFill = '';

  ngOnChanges() {
    const series = this.series?.[0];
    this.question = series?.name ?? '';
    this.countFill = tint(this.palette.primary, PILL_TINT);

    const verbatims = series?.verbatims ?? [];
    this.answered = verbatims.length;
    this.capped = (series?.base ?? 0) > verbatims.length;
    this.answers = group(verbatims);

    const extra = this.block.reportType === 'open-end-explorer'
      ? this.block.reportExtraData
      : undefined;
    const size = Math.trunc(extra?.pageSize ?? DEFAULT_PAGE_SIZE);
    this.pageSize = size > 0 ? size : DEFAULT_PAGE_SIZE;
    this.showSearch = extra?.searchable !== false && this.answers.length >= SEARCH_WORTH_IT;
    this.questionLine = this.questionLineFor();

    // The count is of respondents, capped or not: it is the base the reader is looking at.
    const total = this.series?.[0]?.base ?? this.answered;
    this.countLine = this.answered ? `${total} ${total === 1 ? 'response' : 'responses'}` : '';

    // The filter rail changes the answers under this module, and a search left over from the
    // previous cut would silently hide most of the new one.
    this.term = '';
    this.page = 0;
    this.apply();
  }

  search(term: string) {
    this.term = term;
    // Back to the first page: page 4 of the old result is rarely a page at all of the new one, and
    // an empty list under a search that plainly matches something reads as broken.
    this.page = 0;
    this.apply();
  }

  turn(by: number) {
    this.page = Math.min(Math.max(this.page + by, 0), this.pages - 1);
    this.paginate();
  }

  private apply() {
    const needle = this.term.trim().toLowerCase();
    this.matching = needle
      ? this.answers.filter(answer => answer.text.toLowerCase().includes(needle))
      : this.answers;
    // Rebuilt rather than stored, because the parts depend on the term and not on the answer.
    this.matching = this.matching.map(answer => ({ ...answer, parts: mark(answer.text, needle) }));
    this.paginate();
    this.note = this.noteFor();
  }

  private paginate() {
    this.pages = Math.max(1, Math.ceil(this.matching.length / this.pageSize));
    this.page = Math.min(this.page, this.pages - 1);
    const from = this.page * this.pageSize;
    this.shown = this.matching.slice(from, from + this.pageSize);
    this.range = this.matching.length
      ? `${from + 1}-${from + this.shown.length} of ${this.matching.length}`
      : '';
  }

  /**
   * The question, unless the card is already titled with it. Compared loosely, because a title is
   * often the question with its trailing question mark or its "Q24." prefix trimmed off.
   */
  private questionLineFor(): string {
    if (!this.question) return '';
    const header = (this.block.header ?? '').trim();
    if (!header) return this.question;
    const plain = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const title = plain(header);
    const asked = plain(this.question);
    return title && (asked.includes(title) || title.includes(asked)) ? '' : this.question;
  }

  /**
   * What the title cannot say. The search count when one is running, because "12 of 460" answers
   * the question the reader just asked; the cap whenever it bites, because a list that is quietly
   * part of the answers is a list nobody can trust.
   */
  private noteFor(): string {
    if (!this.answered) {
      return this.series?.[0]?.known === false
        ? 'This module needs an open-ended question.'
        : 'Nobody answered this question in the current view.';
    }

    const capped = this.capped
      ? `the first ${MAX_VERBATIMS} of ${this.series[0].base} are shown`
      : '';
    const term = this.term.trim();
    if (term) {
      const matched = this.matching.length
        ? `${this.matching.length} of ${this.answers.length} answers match "${term}"`
        : `No answer here contains "${term}".`;
      return capped ? `${matched} — ${capped}` : matched;
    }
    return capped ? `Only ${capped}.` : '';
  }
}

/**
 * Identical answers folded together, most-repeated first.
 *
 * Sorted rather than left in the order respondents arrived, because otherwise the counts are
 * invisible: the handful of answers several people gave are exactly what a reader wants off the top
 * of this module, and finding them by scrolling four hundred rows is not reading. Everything said
 * once keeps its original order behind them, so the tail is still the raw list.
 */
function group(verbatims: readonly string[]): Answer[] {
  const seen = new Map<string, Answer>();
  for (const text of verbatims) {
    const key = sameAnswer(text);
    const already = seen.get(key);
    if (already) already.count++;
    else seen.set(key, { text, count: 1, parts: [{ text, hit: false }] });
  }
  // Stable within a count: Map keeps insertion order and sort() is stable, so two answers given
  // twice each stay in the order the respondents gave them.
  return [...seen.values()].sort((a, b) => b.count - a.count);
}

/**
 * The answer split around every occurrence of the term, as text rather than as HTML.
 *
 * Deliberately not an innerHTML highlight: this is respondent-typed text, and the one thing never to
 * do with it is hand it to the browser as markup. The template marks the parts flagged here.
 */
function mark(text: string, needle: string): { text: string; hit: boolean }[] {
  if (!needle) return [{ text, hit: false }];

  const parts: { text: string; hit: boolean }[] = [];
  const haystack = text.toLowerCase();
  let at = 0;
  for (;;) {
    const found = haystack.indexOf(needle, at);
    if (found === -1) break;
    if (found > at) parts.push({ text: text.slice(at, found), hit: false });
    parts.push({ text: text.slice(found, found + needle.length), hit: true });
    at = found + needle.length;
  }
  if (at < text.length) parts.push({ text: text.slice(at), hit: false });
  return parts;
}
